<template>
  <div class="last-contact">
    <span class="last-contact-label">{{ selfContent.label }}</span>
    <div class="last-contact-bar">
      <span class="last-contact-icon" aria-hidden="true">
        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"/><path d="M12 6v6l4 2"/></svg>
      </span>
      <span class="last-contact-text">
        <template v-if="displayDate">
          <strong>{{ displayDate }}</strong>
          <span class="last-contact-ago">· {{ relativeText }}</span>
        </template>
        <span v-else class="last-contact-empty">Aucun contact enregistré</span>
      </span>
      <span class="last-contact-actions">
        <button type="button" class="last-contact-button" :disabled="selfContent.readOnly" @click="setToday">Aujourd’hui</button>
        <button type="button" class="last-contact-button" :disabled="selfContent.readOnly" @click="openPicker">
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M8 2v4"/><path d="M16 2v4"/><rect width="18" height="18" x="3" y="4" rx="2"/><path d="M3 10h18"/></svg>
          Changer
        </button>
        <button v-if="isoDay" type="button" class="last-contact-clear" title="Effacer la date" aria-label="Effacer la date du dernier contact" :disabled="selfContent.readOnly" @click="clear">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M18 6 6 18"/><path d="m6 6 12 12"/></svg>
        </button>
      </span>
      <!-- Sélecteur de date natif, ouvert par le bouton « Changer » -->
      <input ref="dateInput" class="last-contact-native" type="date" tabindex="-1" aria-hidden="true" :value="isoDay" @change="onPicked"/>
    </div>
  </div>
</template>

<script>
const DAY_IN_MS = 24 * 60 * 60 * 1000;

function toIsoDay(date) {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
}

export default {
  name: 'ClientLastContact',
  props: ['content'],
  data() {
    return {
      selfContent: this.content
    };
  },
  computed: {
    // Jour du dernier contact au format AAAA-MM-JJ (la valeur peut venir du serveur avec l'heure)
    isoDay() {
      const value = this.selfContent.value;
      return typeof value === 'string' && /^\d{4}-\d{2}-\d{2}/.test(value) ? value.slice(0, 10) : '';
    },
    day() {
      return this.isoDay ? new Date(`${this.isoDay}T12:00:00`) : null;
    },
    displayDate() {
      return this.day && !isNaN(this.day) ? this.day.toLocaleDateString('fr-FR') : '';
    },
    relativeText() {
      const today = new Date();
      today.setHours(12, 0, 0, 0);
      const days = Math.round((today - this.day) / DAY_IN_MS);
      if (days === 0) { return "aujourd’hui" }
      if (days === 1) { return 'hier' }
      if (days > 1) { return `il y a ${days} jours` }
      if (days === -1) { return 'demain' }
      return `dans ${-days} jours`;
    }
  },
  methods: {
    setToday() {
      if (this.selfContent.readOnly) { return }
      this.selfContent.value = toIsoDay(new Date());
    },
    openPicker() {
      const input = this.$refs.dateInput;
      if (this.selfContent.readOnly || !input) { return }
      if (typeof input.showPicker === 'function') { input.showPicker() } else { input.focus(); input.click() }
    },
    onPicked(event) {
      if (event.target.value) { this.selfContent.value = event.target.value }
    },
    clear() {
      if (this.selfContent.readOnly) { return }
      this.selfContent.value = '';
    }
  }
}
</script>

<style lang="stylus" scoped>
@require '~/src/common/style/common.styl';

.last-contact
  min-width 0

.last-contact-label
  display block
  margin 0 0 6px 2px
  font-size 0.82em
  font-weight 600
  color CTRL_LABEL

.last-contact-bar
  position relative
  display flex
  align-items center
  gap 10px
  box-sizing border-box
  min-height CTRL_HEIGHT
  padding 0 6px 0 CTRL_PADDING_X
  background-color white
  border 1px solid CTRL_BORDER
  border-radius CTRL_RADIUS

.last-contact-icon
  display flex
  flex 0 0 auto
  color CTRL_LABEL

.last-contact-text
  flex 1 1 auto
  min-width 0
  overflow hidden
  text-overflow ellipsis
  white-space nowrap
  strong
    font-weight 700
    color CTRL_TEXT

.last-contact-ago, .last-contact-empty
  font-size 0.9em
  color CTRL_TEXT_MUTED

.last-contact-actions
  display flex
  align-items center
  gap 6px
  flex 0 0 auto

.last-contact-button
  display inline-flex
  align-items center
  gap 6px
  box-sizing border-box
  height 30px
  padding 0 12px
  font inherit
  font-size 0.86em
  font-weight 700
  color THEME_COLOR_PRIMARY
  white-space nowrap
  background-color white
  border 1.5px solid #cfd8e8
  border-radius 999px
  cursor pointer
  transition background-color CTRL_EASE, border-color CTRL_EASE
  &:hover:not(:disabled)
    background-color rgba(THEME_COLOR_PRIMARY, 0.06)
    border-color THEME_COLOR_PRIMARY_FADDED
  &:focus-visible
    outline none
    box-shadow 0 0 0 2px white, 0 0 0 4px CTRL_FOCUS
  &:disabled
    cursor auto
    color CTRL_DISABLED_TEXT

.last-contact-clear
  display flex
  align-items center
  justify-content center
  width 28px
  height 28px
  padding 0
  color CTRL_TEXT_MUTED
  background transparent
  border none
  border-radius 50%
  cursor pointer
  &:hover:not(:disabled)
    background-color rgba(THEME_COLOR_PRIMARY, 0.08)
  &:focus-visible
    outline none
    box-shadow 0 0 0 2px CTRL_FOCUS

.last-contact-native
  position absolute
  right 60px
  bottom 0
  width 1px
  height 1px
  padding 0
  border 0
  opacity 0
  pointer-events none

@media only screen and (max-width: TABLET_SIZE)
  .last-contact-bar
    flex-wrap wrap
    padding 6px 8px 6px CTRL_PADDING_X
  .last-contact-actions
    flex 1 0 100%
</style>
